/**
 * Canonical source classifications for `lm_registry.source_type`.
 *
 * Mirrors `workd_mission_control.lm_registry.source_type` plus the
 * local-only `KYC` bucket (Know-Your-Customer compliance documentation
 * that doesn't correspond to a regulator-published source). `OTHER` is
 * the safety valve for values that ERP imports surfaced before this enum
 * existed.
 *
 * **TWO different canonical forms exist for "what kind of license":**
 *   - `lm_registry.source_type` uses the SCREAMING_SNAKE_CASE values
 *     defined here (`STATE_BOARD`, `NPI`, etc.). The LM scrapers write
 *     these directly.
 *   - `client_license.type` uses the TITLE-CASE LABEL form (`State Board`,
 *     `NPI`, `Controlled Substance`, etc.) because the legacy SM1
 *     checkout/validation flow reads this column with `WHERE type = 'State Board'`
 *     case-sensitively. Writers that produce a value for `client_license.type`
 *     MUST use `normalizeClientLicenseType()` (NOT `normalizeLicenseSourceType()`).
 *
 * See [docs/claude/licensing-cleanup-defects.md](../../../../docs/claude/licensing-cleanup-defects.md)
 * for the 2026-05-27 incident where a `STATE_BOARD`-normalizing migration
 * broke the SM1 checkout validation in production.
 */
export const LICENSE_SOURCE_TYPES = [
	'STATE_BOARD',
	'NPI',
	'DEA',
	'DEA_CMEA',
	'STATE_DRUG_PERMIT',
	'CONTROLLED_SUBSTANCE',
	'TOBACCO',
	'LIQUOR',
	'KYC',
	'OTHER',
] as const;

export type LicenseSourceType = (typeof LICENSE_SOURCE_TYPES)[number];

/**
 * Human-readable display labels for the License Information card on the
 * license detail page, the licenses-list "Type" column, and the Type
 * filter dropdown.
 *
 * Add a new label here when adding a new value to LICENSE_SOURCE_TYPES.
 */
export const LICENSE_SOURCE_TYPE_LABEL: Readonly<
	Record<LicenseSourceType, string>
> = {
	STATE_BOARD: 'State Board',
	NPI: 'NPI',
	DEA: 'DEA',
	DEA_CMEA: 'DEA CMEA',
	STATE_DRUG_PERMIT: 'State Drug Permit',
	CONTROLLED_SUBSTANCE: 'Controlled Substance',
	TOBACCO: 'Tobacco',
	LIQUOR: 'Liquor',
	KYC: 'KYC',
	OTHER: 'Other',
} as const;

/**
 * Resolve any stored value (canonical or legacy free-text) to a
 * `LicenseSourceType`. Maps the legacy free-text strings that were in
 * `client_license.type` before B4 — used by the backfill migration AND
 * as a runtime safety net for any read path that still encounters a
 * legacy value before its tenant DB has been backfilled.
 *
 * Pass `null` / `undefined` / empty / unknown → returns `'OTHER'`.
 */
export function normalizeLicenseSourceType(
	raw: string | null | undefined,
): LicenseSourceType {
	if (raw == null) {
		return 'OTHER';
	}
	const upper = raw.trim().toUpperCase();
	if (upper === '') {
		return 'OTHER';
	}
	// Direct canonical hit
	if ((LICENSE_SOURCE_TYPES as ReadonlyArray<string>).includes(upper)) {
		return upper as LicenseSourceType;
	}
	// Legacy variants
	if (upper === 'STATE BOARD' || upper === 'STATEBOARD') {
		return 'STATE_BOARD';
	}
	if (upper.includes('PHARMACY')) {
		// "PHARMACY" / "RETAIL PHARMACY" / "In-State Pharmacy" all came
		// in as STATE_BOARD-sourced licenses historically.
		return 'STATE_BOARD';
	}
	if (
		upper === 'CONTROLLED SUBSTANCE' ||
		upper === 'CONTROLLED-SUBSTANCE' ||
		upper === 'CSR'
	) {
		return 'CONTROLLED_SUBSTANCE';
	}
	// DEA CMEA self-certification (PD-13953) — its own facet, NOT 'DEA':
	// a CMEA self-cert is not a DEA registration and must never satisfy a
	// DEA-registration check.
	if (upper.includes('CMEA') || upper.includes('METHAMPHETAMINE')) {
		return 'DEA_CMEA';
	}
	// State names appearing in `type` are an ERP import bug — they
	// almost always corresponded to a state-board license that was
	// mislabeled with the state name. Map to STATE_BOARD.
	if (
		upper === 'FLORIDA' ||
		upper === 'CALIFORNIA' ||
		upper === 'TEXAS' ||
		upper === 'NEW YORK' ||
		upper === 'NEW JERSEY' ||
		upper.length === 2 // ISO state codes accidentally written here
	) {
		return 'STATE_BOARD';
	}
	return 'OTHER';
}

/**
 * Convenience for display surfaces — resolves the canonical value first
 * (handles legacy strings) and then returns the human label.
 */
export function getLicenseSourceTypeLabel(
	raw: string | null | undefined,
): string {
	return LICENSE_SOURCE_TYPE_LABEL[normalizeLicenseSourceType(raw)];
}

/**
 * Canonical form for `client_license.type` — TITLE-CASE labels (NOT the
 * SCREAMING_SNAKE_CASE used in `lm_registry.source_type`). Use this for
 * EVERY write to `client_license.type`. See the comment at the top of
 * this file for the two-form story.
 *
 * Reads through `normalizeLicenseSourceType()` first to canonicalize any
 * legacy free-text input, then returns the matching label
 * (e.g. `'State Board'`, `'Controlled Substance'`). Acronym labels
 * (`'NPI'`, `'DEA'`, `'KYC'`) are stable across both forms.
 *
 * Pass `null` / `undefined` / empty / unknown → returns `'Other'`.
 */
export function normalizeClientLicenseType(
	raw: string | null | undefined,
): string {
	return LICENSE_SOURCE_TYPE_LABEL[normalizeLicenseSourceType(raw)];
}

/**
 * Resolve a stored `client_license.type` value to its canonical
 * SCREAMING_SNAKE `LicenseSourceType` key — the form used as the option
 * `value` in the license-editor type dropdown. Use this wherever a stored /
 * display license type has to line up with the dropdown options (editor
 * pre-selection, duplicate-type detection).
 *
 * Resolution order: exact title-case label match FIRST — this round-trips
 * every `LICENSE_SOURCE_TYPE_LABEL` value, including multi-word labels such
 * as `'State Drug Permit'` that `normalizeLicenseSourceType()`'s legacy
 * free-text heuristics don't special-case (they'd otherwise fall through to
 * `'OTHER'`) — then fall back to that normalizer for the SCREAMING_SNAKE
 * keys and legacy free-text.
 */
export function licenseSourceTypeKeyFromStored(
	raw: string | null | undefined,
): LicenseSourceType {
	if (raw != null) {
		const exactLabelMatch = (
			Object.entries(LICENSE_SOURCE_TYPE_LABEL) as Array<
				[LicenseSourceType, string]
			>
		).find(([, label]) => label === raw);

		if (exactLabelMatch != null) {
			return exactLabelMatch[0];
		}
	}

	return normalizeLicenseSourceType(raw);
}

/**
 * Whether a license type may have MULTIPLE licenses on the same client
 * address. Only `OTHER` qualifies: it is the catch-all bucket that
 * `normalizeLicenseSourceType()` funnels every unrecognized value into, so a
 * single address can legitimately hold several "Other" licenses. Every
 * specific type (DEA, NPI, State Board, …) stays one-per-address. (PD-13820)
 *
 * Accepts any stored form — title-case label, SCREAMING_SNAKE key, or legacy
 * free-text — so the server (which reads the title-case `client_license.type`)
 * and the editor (which reads the dropdown's SCREAMING_SNAKE keys) share one
 * rule.
 */
export function allowsMultipleLicensesPerAddress(
	raw: string | null | undefined,
): boolean {
	return licenseSourceTypeKeyFromStored(raw) === 'OTHER';
}

/**
 * Per-source-type "how is proof captured?" config. The UI uses this to
 * decide what affordance to render on suggestion cards, confirmed cards,
 * and audit rows:
 *
 *   - `per-license-file`  Capture is a portal page screenshot/PDF stored
 *                         in lm_files keyed to each license. Show a
 *                         "View File" button (or "No File Available"
 *                         when missing). STATE_BOARD, DEA, CONTROLLED_
 *                         SUBSTANCE, TOBACCO, LIQUOR.
 *
 *   - `bulk-import`       Capture is a bulk-data import; the data IS the
 *                         proof. Show a "Sourced from {label}" badge
 *                         with `sourceUrl` as the data-provider's home
 *                         page (link out via tooltip / info icon). No
 *                         per-license file. NPI is the only one today
 *                         (CMS NPPES CSV downloads).
 *
 * Adding a new LICENSE_SOURCE_TYPE means adding an entry here too —
 * the UI defaults to a safe fallback when the entry is missing, but
 * cards will look generic. Keep the maps aligned.
 */
export type LicenseProofModel =
	| { kind: 'per-license-file'; label: string }
	| { kind: 'bulk-import'; label: string; sourceUrl: string };

export const LICENSE_SOURCE_TYPE_PROOF_MODEL: Readonly<
	Record<LicenseSourceType, LicenseProofModel>
> = {
	STATE_BOARD: {
		kind: 'per-license-file',
		label: 'State Board Proof',
	},
	NPI: {
		kind: 'bulk-import',
		label: 'CMS NPPES',
		sourceUrl: 'https://download.cms.gov/nppes/NPI_Files.html',
	},
	DEA: { kind: 'per-license-file', label: 'DEA Proof' },
	DEA_CMEA: {
		kind: 'bulk-import',
		label: 'DEA CMEA Self-Certification List',
		sourceUrl: 'https://www.deadiversion.usdoj.gov/meth/self_cert.html',
	},
	STATE_DRUG_PERMIT: {
		kind: 'per-license-file',
		label: 'State Drug Permit Proof',
	},
	CONTROLLED_SUBSTANCE: {
		kind: 'per-license-file',
		label: 'Controlled Substance Proof',
	},
	TOBACCO: { kind: 'per-license-file', label: 'Tobacco Proof' },
	LIQUOR: { kind: 'per-license-file', label: 'Liquor Proof' },
	KYC: { kind: 'per-license-file', label: 'KYC Document' },
	OTHER: { kind: 'per-license-file', label: 'Proof' },
} as const;

/**
 * Convenience accessor for the proof model with legacy-value tolerance —
 * mirrors `getLicenseSourceTypeLabel`. Always returns a valid model.
 */
export function getLicenseProofModel(
	raw: string | null | undefined,
): LicenseProofModel {
	return LICENSE_SOURCE_TYPE_PROOF_MODEL[normalizeLicenseSourceType(raw)];
}
